/* Comprehensive explanation screen for all AI-powered features in FeedKiwi.
   Covers KiwiAI chat, daily digest, smart cleaner, content analysis,
   provocative detection, goal suggestions, topics graph, section groupings,
   For You tab, and auto-plan. */

import {
  Bot,
  Sparkles,
  FileText,
  Trash2,
  Brain,
  ShieldAlert,
  Target,
  Network,
  LayoutGrid,
  Heart,
  Calendar,
  MessageSquare,
  BookOpen,
  Bookmark,
  Highlighter,
  AudioLines,
  SkipForward,
  Lightbulb,
  Wand2,
  Zap,
  ChevronRight,
} from "lucide-react";
import {
  FeatureInfoDialog,
  InfoSection,
  InfoSectionTitle,
  InfoHeroBadge,
} from "./FeatureInfoDialog";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

interface AIFeaturesInfoProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function AIFeatureCard({
  icon,
  iconColor,
  iconBg,
  title,
  description,
  capabilities,
  howTo,
}: {
  icon: ReactNode;
  iconColor: string;
  iconBg: string;
  title: string;
  description: string;
  capabilities?: string[];
  howTo?: string;
}) {
  return (
    <InfoSection>
      <InfoSectionTitle icon={icon} iconColor={iconColor} iconBg={iconBg}>
        {title}
      </InfoSectionTitle>
      <p className="text-xs text-muted-foreground leading-relaxed mb-3">
        {description}
      </p>
      {capabilities && capabilities.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {capabilities.map((cap) => (
            <div key={cap} className="flex items-start gap-2">
              <ChevronRight
                className={cn("size-3 shrink-0 mt-0.5", iconColor)}
              />
              <span className="text-[11px] text-muted-foreground leading-snug">
                {cap}
              </span>
            </div>
          ))}
        </div>
      )}
      {howTo && (
        <div className="rounded-lg bg-muted/40 border border-border/30 px-3 py-2">
          <p className="text-[10px] text-muted-foreground">
            <span className="font-semibold text-foreground">How to use: </span>
            {howTo}
          </p>
        </div>
      )}
    </InfoSection>
  );
}

function AIHeroVisual() {
  const features = [
    { icon: <Bot className="size-3" />, label: "Chat", color: "text-ai-accent" },
    { icon: <FileText className="size-3" />, label: "Digest", color: "text-chart-1" },
    { icon: <Brain className="size-3" />, label: "Analysis", color: "text-chart-2" },
    { icon: <Trash2 className="size-3" />, label: "Cleaner", color: "text-chart-3" },
    { icon: <Network className="size-3" />, label: "Topics", color: "text-chart-4" },
    { icon: <Target className="size-3" />, label: "Goals", color: "text-chart-5" },
  ];

  return (
    <div className="flex flex-wrap justify-center gap-2 max-w-[300px] mx-auto">
      {features.map((f) => (
        <div
          key={f.label}
          className="flex items-center gap-1.5 rounded-full border border-border/50 bg-card px-2.5 py-1"
        >
          <span className={f.color}>{f.icon}</span>
          <span className="text-[10px] font-medium">{f.label}</span>
        </div>
      ))}
    </div>
  );
}

export function AIFeaturesInfo({ open, onOpenChange }: AIFeaturesInfoProps) {
  return (
    <FeatureInfoDialog
      open={open}
      onOpenChange={onOpenChange}
      title="AI Features"
    >
      <div className="space-y-5">
        {/* Hero */}
        <div className="text-center space-y-3">
          <InfoHeroBadge>
            <Sparkles className="size-3" />
            Powered by AI
          </InfoHeroBadge>
          <h2 className="text-xl font-bold tracking-tight">
            AI Features
          </h2>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
            FeedKiwi uses AI across the entire app to help you consume
            content more intentionally — from smart summaries to
            personalized recommendations.
          </p>
          <AIHeroVisual />
        </div>

        {/* ── 1. KiwiAI Chat ── */}
        <AIFeatureCard
          icon={<Bot className="size-4" />}
          iconColor="text-ai-accent"
          iconBg="bg-ai-accent/12"
          title="KiwiAI Chat"
          description="A built-in AI assistant that understands your entire content library. Search, organize, and manage everything through natural conversation."
          capabilities={[
            "Search your feed, queue, and backlog with natural language",
            "Move, queue, save to backlog, complete, or delete items by description",
            "Create and manage tag goals and sources",
            "Get stats and summaries of your consumption",
            "Ask questions about how FeedKiwi works",
            "Ask questions about specific content (videos, podcasts, articles) when content analysis is enabled",
            "Launch guided walkthroughs for any feature",
          ]}
          howTo='Tap the chat button in the bottom-right corner. Type naturally — "find my Python tutorials" or "create a learning goal for 30 min/day."'
        />

        {/* ── 2. Daily Digest ── */}
        <AIFeatureCard
          icon={<FileText className="size-4" />}
          iconColor="text-chart-1"
          iconBg="bg-chart-1/12"
          title="AI Daily Digest"
          description="An AI-generated summary of your feed items grouped by topic, with key takeaways and reading time estimates. Available in three detail levels."
          capabilities={[
            "Summarizes your feed into topic-grouped key points",
            "Cites specific items so you can jump to them",
            "Flags provocative or clickbait content (see below)",
            "Suggests items that will help you hit your daily goals",
            "Three styles: Concise, Regular, Comprehensive",
            "Can auto-generate after every feed update",
          ]}
          howTo="Open the digest banner at the top of the Feed page. Choose a style and tap Generate. Enable auto-generate in Settings."
        />

        {/* ── 3. Smart Feed Cleaner ── */}
        <AIFeatureCard
          icon={<Wand2 className="size-4" />}
          iconColor="text-chart-3"
          iconBg="bg-chart-3/12"
          title="Smart Feed Cleaner"
          description="Tell the AI what you care about and it will sort through your feed — grouping items and recommending what to queue vs. dismiss, with reasons."
          capabilities={[
            "Describe your criteria in plain language",
            "AI groups items by relevance and recommends actions",
            "Review suggestions and apply in bulk",
            "Great for triaging a large backlog of feed items",
          ]}
          howTo='Tap "Smart Clean" on the Feed page. Describe what you want to focus on and let the AI sort through everything.'
        />

        {/* ── 4. Content Analysis ── */}
        <AIFeatureCard
          icon={<Brain className="size-4" />}
          iconColor="text-chart-2"
          iconBg="bg-chart-2/12"
          title="Content Analysis"
          description="AI reads your videos, podcasts, and articles to extract structured insights — chapters, highlights, references, and topics."
          capabilities={[
            "Extracts chapters and sections from videos and podcasts",
            "Highlights key quotes and takeaways",
            "Surfaces references (books, links, people mentioned)",
            "Detects topics for the knowledge graph",
            "Podcast transcription for full-text search",
            "Auto-skip detection for intros, outros, and sponsors",
            "Can auto-analyze queued items in the background",
          ]}
          howTo="Enable in Settings > Content Analysis. Results appear in the video/podcast player details panel and the article reader insights panel."
        />

        {/* ── 5. Saved References ── */}
        <InfoSection>
          <InfoSectionTitle
            icon={<Bookmark className="size-4" />}
            iconColor="text-chart-4"
            iconBg="bg-chart-4/12"
          >
            AI-Surfaced References
          </InfoSectionTitle>
          <p className="text-xs text-muted-foreground leading-relaxed mb-2">
            When content analysis is enabled, the AI finds books, articles,
            tools, and people mentioned in your content. Save the ones that
            matter and browse them later.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {[
              { icon: <BookOpen className="size-2.5" />, label: "Books" },
              { icon: <Highlighter className="size-2.5" />, label: "Key quotes" },
              { icon: <Lightbulb className="size-2.5" />, label: "Ideas" },
            ].map((t) => (
              <span
                key={t.label}
                className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-chart-4/10 text-chart-4 border border-chart-4/20 flex items-center gap-1"
              >
                {t.icon} {t.label}
              </span>
            ))}
          </div>
          <div className="rounded-lg bg-muted/40 border border-border/30 px-3 py-2 mt-3">
            <p className="text-[10px] text-muted-foreground">
              <span className="font-semibold text-foreground">
                How to use:{" "}
              </span>
              Save references from the player/reader panels. Browse them under
              Insights &rarr; References.
            </p>
          </div>
        </InfoSection>

        {/* ── 6. Provocative Content Detection ── */}
        <AIFeatureCard
          icon={<ShieldAlert className="size-4" />}
          iconColor="text-destructive"
          iconBg="bg-destructive/12"
          title="Provocative Content Detection"
          description="The AI flags clickbait, sensationalized, or provocative items in your feed so you can stay focused on quality content."
          capabilities={[
            "Automatically flags provocative items during digest generation",
            "Dedicated Provocative tab to review flagged content",
            "Global toggle to hide provocative items from your feed",
            "Per-source opt-out if a source gets false positives",
          ]}
          howTo="Enable 'Hide provocative content' in Settings. Flagged items are moved to a separate tab. Exclude specific sources from the filter in their settings."
        />

        {/* ── 7. AI Goal Suggestions ── */}
        <AIFeatureCard
          icon={<Target className="size-4" />}
          iconColor="text-chart-5"
          iconBg="bg-chart-5/12"
          title="AI Goal Suggestions"
          description="When you generate a daily digest, the AI looks at your remaining tag goals and suggests specific feed items that would help you hit your targets."
          capabilities={[
            "Analyzes your remaining goal minutes for the day",
            "Picks items from your feed that match goal tags",
            "One-tap to add all suggestions to Today's Plan",
          ]}
          howTo='Generate a digest on the Feed page — the "Hit your goals" section shows AI-picked items you can add to your plan.'
        />

        {/* ── 8. Topics Graph ── */}
        <AIFeatureCard
          icon={<Network className="size-4" />}
          iconColor="text-chart-4"
          iconBg="bg-chart-4/12"
          title="Topics Knowledge Graph"
          description="AI extracts topics from your analyzed content and builds an interactive graph showing how your knowledge areas connect."
          capabilities={[
            "Visual network of topics you've consumed",
            "Nodes sized by frequency, edges by co-occurrence",
            "Click any topic to see all related items",
            "Discover unexpected connections between interests",
          ]}
          howTo="Go to Insights > Topics tab. Pan and zoom the graph. Click a topic node to explore items."
        />

        {/* ── 9. AI Section Groupings ── */}
        <AIFeatureCard
          icon={<LayoutGrid className="size-4" />}
          iconColor="text-primary"
          iconBg="bg-primary/12"
          title="AI Topic Sections"
          description="AI can automatically organize your tags into named sections, giving your feed and queue a magazine-style layout grouped by topic."
          capabilities={[
            "One-click auto-generation from your existing tags",
            "Groups are fully editable after generation",
            "Feed and Queue items are organized into named sections",
          ]}
          howTo='Go to Settings > Topic Sections and tap the sparkles icon to auto-generate. Edit the groupings to your liking.'
        />

        {/* ── 10. For You Tab ── */}
        <AIFeatureCard
          icon={<Heart className="size-4" />}
          iconColor="text-pink-500"
          iconBg="bg-pink-500/12"
          title="For You Recommendations"
          description="A personalized feed tab that ranks sources based on your actual completion patterns — surfacing content you consistently enjoy."
          capabilities={[
            "Learns from which sources you complete most often",
            "Shows items from your highest-engagement sources first",
            "Explains why each source was recommended",
          ]}
          howTo='Switch to the "For You" tab on the Feed page. It appears once you have enough completion history.'
        />

        {/* ── 11. AI Auto-Plan ── */}
        <AIFeatureCard
          icon={<Calendar className="size-4" />}
          iconColor="text-chart-2"
          iconBg="bg-chart-2/12"
          title="AI Auto-Plan"
          description="Write custom instructions and the AI will suggest a daily queue plan based on your goals, preferences, and consumption history."
          capabilities={[
            "Set custom instructions describing your daily preferences",
            "AI selects and schedules items from your queue",
            "Respects your tag goals and time constraints",
          ]}
          howTo="Configure auto-plan instructions in Settings or ask KiwiAI to set up auto-planning for you."
        />

        {/* ── 12. Auto-Skip & Playback ── */}
        <InfoSection>
          <InfoSectionTitle
            icon={<SkipForward className="size-4" />}
            iconColor="text-chart-3"
            iconBg="bg-chart-3/12"
          >
            Smart Playback
          </InfoSectionTitle>
          <p className="text-xs text-muted-foreground leading-relaxed mb-2">
            AI detects intro, outro, and sponsor segments in videos and
            podcasts. Enable auto-skip to jump past them automatically,
            saving time on every piece of content.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {[
              { icon: <SkipForward className="size-2.5" />, label: "Skip intros" },
              { icon: <SkipForward className="size-2.5" />, label: "Skip outros" },
              { icon: <AudioLines className="size-2.5" />, label: "Skip sponsors" },
            ].map((t) => (
              <span
                key={t.label}
                className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-chart-3/10 text-chart-3 border border-chart-3/20 flex items-center gap-1"
              >
                {t.icon} {t.label}
              </span>
            ))}
          </div>
        </InfoSection>

        {/* Summary: KiwiAI can do it all */}
        <div className="rounded-xl bg-ai-accent/5 border border-ai-accent/15 p-4">
          <div className="flex items-start gap-3">
            <div className="size-8 rounded-lg bg-ai-accent/12 flex items-center justify-center shrink-0">
              <MessageSquare className="size-4 text-ai-accent" />
            </div>
            <div>
              <p className="text-xs font-semibold">
                KiwiAI ties it all together
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                Most AI features can be triggered through the chat. Ask
                KiwiAI to generate a digest, clean your feed, explain your
                stats, or set up goals — all from one conversation.
              </p>
            </div>
          </div>
        </div>

        {/* Settings tip */}
        <div className="rounded-xl bg-muted/30 border border-border/40 p-4">
          <div className="flex items-start gap-3">
            <div className="size-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
              <Zap className="size-4 text-muted-foreground" />
            </div>
            <div>
              <p className="text-xs font-semibold">All in Settings</p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                Every AI feature can be toggled individually in Settings.
                Enable content analysis, auto-digest, auto-skip, provocative
                filtering, and more — each on its own switch.
              </p>
            </div>
          </div>
        </div>
      </div>
    </FeatureInfoDialog>
  );
}
